import { CommonModule } from '@angular/common'
import {
  Component,
  ElementRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'

import { Store } from '@ngrx/store'
import { last } from 'lodash/fp'
import { DialogModule } from 'primeng/dialog'
import { ImageModule } from 'primeng/image'
import { ProgressBarModule } from 'primeng/progressbar'
import { TooltipModule } from 'primeng/tooltip'

import { filter, take, withLatestFrom } from 'rxjs'
import { fromInteropObservable } from 'rxjs/internal/observable/innerFrom'

import { AiWriterApi } from '@flowx/designer/modules/new-ai/agents/writer/api/writer.api'
import { AiAgent, ContextFile } from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import {
  DragType,
  Position,
} from '@flowx/designer/modules/new-ai/common/directives/drag/drag.models'
// eslint-disable-next-line import/no-cycle
import { confirmDiscardContext } from '@flowx/designer/modules/new-ai/common/guards/pending-context.guard'
import { AiIconsModule } from '@flowx/designer/modules/new-ai/common/icons/ai-icons.module'
import { AiCommandPaletteComponent } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command-palette/command-palette.component'
import { CommandPaletteService } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command-palette/command-palette.service'
import { CommandIcon } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command/command.component'
import { AiKeyboardKeyCode } from '@flowx/designer/modules/new-ai/modules/command-palette/components/shortcut/shortcut.component'
import { Shortcuts } from '@flowx/designer/modules/new-ai/modules/command-palette/components/shortcuts/shortcuts.component'
import { AiActions, BreadcrumbActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { KeyboardService } from '@flowx/designer/shared/services/keyboard.service'
import { SwimlaneService } from '@flowx/designer/shared/services/swimlane.service'
import { selectedAiUserTaskTemplateConfig } from '@flowx/designer/shared/store/selectors/template-config.selectors'
import { convertBlobToBase64 } from '@flowx/designer/shared/utils/file.utils'

import { CommandPaletteActions } from '../../../../store/ai.actions'
import { AiSideChatHistoryComponent } from '../side-chat-history/side-chat-history.component'
import { AiSideTypeAreaComponent } from '../side-type-area/side-type-area.component'

const typeAreaHintMap: Partial<Record<AiAgent, string>> = {
  [AiAgent.ASSISTANT]: 'Ask me to explain or do something...',
  [AiAgent.ANALYST]:
    'Describe the Process you want to create in detail, or check one of the examples below',
  [AiAgent.DESIGNER]:
    'Describe the UI you want to create in detail, or check one of the examples below',
  [AiAgent.DEVELOPER]:
    'Describe the business rule you want created or choose one of the examples below',
}
const defaultTypeAreaPlaceholder = 'Find anything or ask FlowX for help'

@Component({
  selector: 'flx-side-chat-window',
  imports: [
    CommonModule,
    DialogModule,
    ImageModule,
    ProgressBarModule,
    AiIconsModule,
    TooltipModule,
    AiSideTypeAreaComponent,
    AiSideChatHistoryComponent,
  ],
  templateUrl: './side-chat.component.html',
  styleUrls: ['./side-chat.component.scss'],
  standalone: true,
})
export class SideChatWindowComponent {
  #commandPaletteService = inject(CommandPaletteService)
  #store = inject(Store)
  #injector = inject(Injector)
  #swimlaneService = inject(SwimlaneService)
  #keyboardService = inject(KeyboardService)
  #AiWriterApiService = inject(AiWriterApi)

  #bpmnReadonly = toSignal<boolean>(fromInteropObservable(BpmnEditorStores.readonly$))

  commandPaletteEl = viewChild(AiCommandPaletteComponent, { read: ElementRef })

  title = input<string>()
  sidePanelMode = input<boolean>(false)

  currentSearchQuery = signal('')
  currentResultItem = signal(null)

  breadcrumbs = this.#store.selectSignal(AiSelectors.breadcrumbs)

  draftMessage = this.#store.selectSignal(AiSelectors.draftMessage)
  initialMessage = this.#store.selectSignal(AiSelectors.initialMessage)
  generatedImage = this.#store.select(AiSelectors.generatedImage)

  hasBreadcrumbs = computed(() => this.breadcrumbs()?.length)
  isBreadcrumbsBackDisabled = this.#store.selectSignal(AiSelectors.isBreadcrumbsBackDisabled)
  typeAreaPlaceholder = computed(
    () =>
      typeAreaHintMap[this.#store.selectSignal(AiSelectors.selectedAgent)()] ||
      defaultTypeAreaPlaceholder
  )
  highlightedCommand = signal(null)
  commands = computed(() => {
    const selectedAgent = this.#store.selectSignal(AiSelectors.selectedAgent)()
    const contextAgent = this.#store.selectSignal(AiSelectors.contextAgent)()
    const aiPermissions = this.#store.selectSignal(AiSelectors.permissions)()
    const bpmnReadonly = this.#bpmnReadonly()
    const bpmnHasSwimlanesOrNodes = this.#swimlaneService.hasSwimlanesOrNodes()
    const businessRuleContext = this.#store.selectSignal(AiSelectors.businessRuleContext)()
    const businessRuleLanguageAiOptions = this.#store.selectSignal(
      AiSelectors.businessRuleLanguageAiOptions
    )()
    const hasDesignerAiUserTaskTemplateConfig = !!this.#store.selectSignal(
      selectedAiUserTaskTemplateConfig
    )()

    const commands = this.#commandPaletteService.getCommandsByState(selectedAgent, {
      contextAgent,
      aiPermissions,
      bpmnReadonly,
      bpmnHasSwimlanesOrNodes,
      businessRuleContext,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    })
    const searchTerm = this.currentSearchQuery().toLowerCase()
    const filteredItems = commands.filter((item) => item.title.toLowerCase().includes(searchTerm))

    return filteredItems
  })

  actions = computed(() => {
    const selectedAgent = this.#store.selectSignal(AiSelectors.selectedAgent)()
    const contextAgent = this.#store.selectSignal(AiSelectors.contextAgent)()
    const pageAgentAction = this.#store.selectSignal(AiSelectors.pageAgentActions)()
    const aiPermissions = this.#store.selectSignal(AiSelectors.permissions)()
    const bpmnReadonly = this.#bpmnReadonly()
    const bpmnHasSwimlanesOrNodes = this.#swimlaneService.hasSwimlanesOrNodes()
    const businessRuleLanguageAiOptions = this.#store.selectSignal(
      AiSelectors.businessRuleLanguageAiOptions
    )()
    const hasDesignerAiUserTaskTemplateConfig = !!this.#store.selectSignal(
      selectedAiUserTaskTemplateConfig
    )()

    const actions = this.#commandPaletteService.getActionsByState(selectedAgent, {
      contextAgent,
      pageAgentAction,
      aiPermissions,
      bpmnReadonly,
      bpmnHasSwimlanesOrNodes,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    })
    const searchTerm = this.currentSearchQuery().toLowerCase()
    const filteredItems = actions.filter((item) => item.title.toLowerCase().includes(searchTerm))
    const currentCommands = this.commands()
    if (currentCommands.length === 0 && filteredItems.length === 0) {
      return [
        {
          id: 'stars-flowx-blue',
          title: '<i>Ask <b>AI Assistant</b> about:</i> ' + this.currentSearchQuery(),
          icon: CommandIcon.Action,
          action: () =>
            this.#store.dispatch(
              CommandPaletteActions.askAssistantCommand({
                question: this.currentSearchQuery(),
                processDoc: true,
              })
            ),
        },
      ]
    }
    return filteredItems
  })

  messages = this.#store.selectSignal(AiSelectors.messages)
  images = computed(() =>
    this.#store
      .selectSignal(AiSelectors.uploadedImageFiles)()
      .map(({ id, file }) => ({
        id,
        name: file.name,
        src$: convertBlobToBase64(file),
      }))
  )
  bpmnFiles = computed(() =>
    this.#store
      .selectSignal(AiSelectors.uploadedBpmnFiles)()
      .map(({ id, file }) => ({
        id,
        name: file.name,
      }))
  )
  showUploadedImages = computed(
    () => !this.#store.selectSignal(AiSelectors.hasSelectedDocument)() && this.images()?.length
  )
  showUploadedBpmnFiles = computed(() => this.bpmnFiles()?.length)
  uploadingDocuments = this.#store.selectSignal(AiSelectors.uploadingDocuments)
  isUploadingDocuments = this.#store.selectSignal(AiSelectors.isUploadingDocuments)
  selectedDocument = this.#store.selectSignal(AiSelectors.selectedDocument)
  showContextDocuments = computed(() => this.isUploadingDocuments() || this.selectedDocument())
  contextDocumentsStatus = computed(() => (this.isUploadingDocuments() ? 'Uploading' : 'Context'))
  contextDocumentsName = computed(
    () => this.uploadingDocuments()?.[0]?.name || this.selectedDocument()?.name
  )
  isCloseContextDocumentDisabled = this.#store.selectSignal(AiSelectors.pendingAiResponse)
  isCommandPaletteWindowMode = signal(false)
  isAiChatWindowMode = signal(true)

  showCommandPaletteTypeArea = computed(
    () =>
      this.isCommandPaletteWindowMode() &&
      this.#store.selectSignal(AiSelectors.hasAssistantAiPermission)()
  )
  showChatTypeArea = computed(() => {
    const isChatConversationAllowed = this.#store.selectSignal(
      AiSelectors.isChatConversationAllowed
    )
    const canGenerateExpression = this.#store.selectSignal(AiSelectors.canGenerateExpression)

    return isChatConversationAllowed() && canGenerateExpression()
  })

  readonly DragType = DragType
  readonly Shortcuts = Shortcuts

  isSearchingDocuments = signal(false)

  searchResult: any[] = []

  constructor() {
    effect(() => {
      const draftMessage = this.draftMessage()
      const initialMessage = this.initialMessage()

      if (draftMessage && !initialMessage) {
        this.#store.dispatch(
          AiActions.initialMessage({
            action_type: draftMessage.payload.action_type,
            message: draftMessage.message,
            selectedAgent: draftMessage.selectedAgent,
          })
        )
      }
    })
    this.#keyboardService.metaG.pipe(takeUntilDestroyed()).subscribe((event) => {
      const generateCommand = this.commands().find(
        (command) =>
          !!command.shortcut?.icons?.find((icon) => icon.name === AiKeyboardKeyCode.GENERATE)
      )
      if (generateCommand && !generateCommand.disabled) {
        event.preventDefault()
        event.stopPropagation()
        generateCommand.action()
      }
    })

    this.#keyboardService.arrowDown
      .pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.isExpanded),
          this.#store.select(AiSelectors.isCommandPaletteWindowMode)
        ),
        filter(
          ([, isExpanded, isCommandPaletteWindowMode]) => isExpanded && isCommandPaletteWindowMode
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const commands = this.commands()
        const highlightedCommand = this.highlightedCommand()

        if (!highlightedCommand || highlightedCommand === last(commands).id) {
          this.highlightedCommand.set(commands[0].id)
        } else {
          const highlightedCommandIndex = commands.findIndex(
            (command) => command.id === highlightedCommand
          )
          this.highlightedCommand.set(commands[highlightedCommandIndex + 1].id)
        }

        this.commandPaletteEl().nativeElement.focus()
      })

    this.#keyboardService.arrowUp
      .pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.isExpanded),
          this.#store.select(AiSelectors.isCommandPaletteWindowMode)
        ),
        filter(
          ([, isExpanded, isCommandPaletteWindowMode]) => isExpanded && isCommandPaletteWindowMode
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const commands = this.commands()
        const highlightedCommand = this.highlightedCommand()

        if (!highlightedCommand || highlightedCommand === commands[0].id) {
          this.highlightedCommand.set(last(commands).id)
        } else {
          const highlightedCommandIndex = commands.findIndex(
            (command) => command.id === highlightedCommand
          )
          this.highlightedCommand.set(commands[highlightedCommandIndex - 1].id)
        }

        this.commandPaletteEl().nativeElement.focus()
      })
    this.#keyboardService.enter
      .pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.isExpanded),
          this.#store.select(AiSelectors.isCommandPaletteWindowMode)
        ),
        filter(
          ([, isExpanded, isCommandPaletteWindowMode]) =>
            isExpanded && isCommandPaletteWindowMode && this.highlightedCommand()
        ),
        takeUntilDestroyed()
      )
      .subscribe(() =>
        this.commands()
          .find((command) => command.id === this.highlightedCommand())
          .action()
      )
  }

  goToObject(object: any): void {
    this.#commandPaletteService.navigateToObject(object)
  }

  confirmRemoveDocumentFromActiveContext(): void {
    runInInjectionContext(this.#injector, () => {
      confirmDiscardContext({
        message: `You are about to lose the generated data in the current session. <br/> Are you sure you want to discard the document?`,
        onSuccess: () => {},
      })
        .pipe(take(1))
        .subscribe()
    })
  }

  onContextDocumentCloseClick(): void {
    if (this.#store.selectSignal(AiSelectors.hasDraft)()) {
      this.confirmRemoveDocumentFromActiveContext()
    } else {
      this.#store.dispatch(AiActions.contextDocumentCloseClick())
    }
  }

  onTypeAreaValue(value: string): void {
    const appId = this.#commandPaletteService.getApplicationId()
    const appVersionId = this.#commandPaletteService.getApplicationVersionId()
    this.currentSearchQuery.set(value)
    if (value.length < 3) {
      this.searchResult = []
      this.isSearchingDocuments.set(false)
      this.currentResultItem.set(null)
      return
    }
    this.isSearchingDocuments.set(true)
    this.#AiWriterApiService
      .searchInDocs({
        query: value,
        application_id: appId,
        application_version: appVersionId,
      })
      .subscribe(
        (response) => {
          this.searchResult = response
            .filter((a) => a.distance > 0.25)
            .sort((a, b) => b.distance - a.distance)
          this.isSearchingDocuments.set(false)
          this.currentResultItem.set(null)
        },
        (error) => {
          console.error('Error', error)
        }
      )
  }

  onSearchResultClick(item: any): void {
    this.currentResultItem.set(item)
  }
  onCloseCurrentResultItem(): void {
    this.currentResultItem.set(null)
  }

  deleteFileFromContext(imageId: ContextFile['id']): void {
    this.#store.dispatch(AiActions.fileRemoveClick({ id: imageId }))
  }

  onBreadcrumbsBack(): void {
    this.#store.dispatch(BreadcrumbActions.backClick())
  }

  getIconByType(type: string): string {
    switch (type) {
      case 'node':
        return 'ph pt-1 text-yellow-500 ph-tree-structure font-bold'
      case 'action':
        return 'ph pt-1 text-yellow-500 ph-code font-bold'
      case 'ui':
        return 'ph pt-1 text-yellow-500 ph-layout font-bold'
      case 'datamodel':
        return 'ph pt-1 text-yellow-500 ph-database font-bold'
      default:
        return 'document'
    }
  }

  #convertPositionToNgStyle(position: Position): Record<string, string> {
    return position
      ? Object.entries(position).reduce(
          (style, [key, value]) => {
            style[key] = `${value}px`
            return style
          },
          {} as Record<string, string>
        )
      : null
  }
}
